/**
 * Сравнение двух версий черновика — 05-ui.md §6, ответ `GET /api/v1/drafts/:id/versions/:n/diff`.
 *
 * ДВА ПОЛОТНА ПРЕВЬЮ, а не разноцветный абзац (решение заказчика 26.08.2026). Слева версия-база
 * с зачёркнутым тем, что убрали; справа версия-цель с выделенным тем, что добавили. Обе
 * стороны рисует ТОТ ЖЕ `MessagePreview`, что и режим чтения: второй рендер поста означал бы
 * «сравнили одно, опубликовали другое», а именно этого сравнение и должно не допускать.
 *
 * Компонент НЕ ходит в сеть: и результат сравнения, и обе модели рендера приходят пропсами.
 * Это не стилистика — владелец загрузки на экране один (`DraftPanel`), и версия, показанная
 * слева, обязана быть той самой, номер которой стоит в ответе маршрута.
 *
 * Обе стороны сравнения — канонические формы (см. шапку services/backend/src/domain/version-diff.ts):
 * в канал уходит каноническая форма, и diff по сырому входу показывал бы человеку не то,
 * что он утверждает.
 *
 * Форма ответа описана здесь ЛОКАЛЬНО, потому что схемы этого маршрута в `@smm/contracts`
 * сегодня нет (в `api/ui.ts` её нет среди прочих), а тип из `services/backend` пакету `ui`
 * недоступен по правилу R4 стража границ. Следствие честное и его надо знать: ответ этого
 * маршрута — единственный, который UI не проверяет схемой в рантайме. Место для схемы —
 * `packages/contracts/src/api/ui.ts`, рядом с `RenderModelSchema`. Отсюда же и проверка
 * `aligned` ниже: раз схемы нет, склейка кусков сверяется с текстами версий явно.
 */

import type { DraftContent, MessageEntity } from '@smm/contracts';
import { type JSX, useMemo } from 'react';
import type { RenderModel } from '../api/types.ts';
import { MessagePreview } from '../preview/MessagePreview.tsx';
import type { DiffMarks, DiffRange } from '../preview/marks.tsx';
import { SEND_MESSAGE_THRESHOLD } from '../preview/validation.ts';
import { fileSize } from './format.ts';
import { IconInfo, IconWarn } from './icons.tsx';
import styles from './VersionDiff.module.css';

export type DiffChunkKind = 'equal' | 'add' | 'del';

export interface DiffTextChunk {
  readonly kind: DiffChunkKind;
  readonly text: string;
}

export interface DiffEntityChange {
  readonly kind: 'add' | 'del';
  readonly entity: MessageEntity;
}

export interface VersionDiffBody {
  readonly text: readonly DiffTextChunk[];
  readonly entities: readonly DiffEntityChange[];
  readonly media_changed: boolean;
  readonly link_preview_changed: boolean;
  readonly method_changed: boolean;
  /** true — тексты разошлись сильнее порога, посимвольный diff заменён двумя блоками. */
  readonly coarse: boolean;
}

export interface VersionDiffResult {
  readonly draft_id: string;
  readonly version_no: number;
  readonly against: number;
  readonly diff: VersionDiffBody;
}

export interface DiffSides {
  /** Что убрали — в смещениях текста базы. */
  readonly base: DiffMarks;
  /** Что добавили — в смещениях текста цели. */
  readonly target: DiffMarks;
  /** Склейка кусков: должна совпасть с текстом соответствующей версии. */
  readonly baseText: string;
  readonly targetText: string;
}

/**
 * Раскладывает куски ответа на две стороны. Куски идут в порядке чтения, поэтому смещения
 * копятся линейно: `equal` двигает оба курсора, `del` — только курсор базы, `add` — только
 * курсор цели. Счёт по `String.length`, то есть в UTF-16 — тех же единицах, в которых
 * считает Bot API; сегментация по графемам на стороне сервера на это не влияет, потому что
 * склейка кусков посимвольно равна исходной строке.
 */
export function diffSides(chunks: readonly DiffTextChunk[]): DiffSides {
  const del: DiffRange[] = [];
  const add: DiffRange[] = [];
  let baseText = '';
  let targetText = '';
  for (const c of chunks) {
    if (c.kind === 'equal') {
      baseText += c.text;
      targetText += c.text;
    } else if (c.kind === 'del') {
      del.push([baseText.length, baseText.length + c.text.length]);
      baseText += c.text;
    } else {
      add.push([targetText.length, targetText.length + c.text.length]);
      targetText += c.text;
    }
  }
  return {
    base: { kind: 'del', ranges: del },
    target: { kind: 'add', ranges: add },
    baseText,
    targetText,
  };
}

/**
 * Русские имена типов сущностей. Показывать `text_link` человеку, который утверждает пост,
 * незачем: тип — это то, что он видит в тексте («ссылка», «цитата»), а не поле Bot API.
 */
const ENTITY_LABEL_RU: Readonly<Record<MessageEntity['type'], string>> = {
  bold: 'жирный',
  italic: 'курсив',
  underline: 'подчёркнутый',
  strikethrough: 'зачёркнутый',
  spoiler: 'скрытый',
  code: 'код',
  pre: 'блок кода',
  text_link: 'ссылка',
  blockquote: 'цитата',
  expandable_blockquote: 'раскрываемая цитата',
};

/**
 * Ключи React из самих данных, а не из индекса массива: индекс запрещён линтом и по существу
 * неверен — при обновлении диффа куски съезжают, и React переиспользовал бы чужой узел.
 * Повторы (две одинаковые сущности возможны) получают суффикс.
 */
function withKeys<T>(items: readonly T[], keyOf: (item: T) => string): { key: string; item: T }[] {
  const seen = new Map<string, number>();
  return items.map((item) => {
    const base = keyOf(item);
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return { key: n === 1 ? base : `${base}#${String(n)}`, item };
  });
}

const entityKey = (c: DiffEntityChange): string =>
  `${c.kind}:${c.entity.type}:${String(c.entity.offset)}:${String(c.entity.length)}`;

/** Способ отправки словами. Машинное имя остаётся в диалоге утверждения — там подписывается оно. */
function methodLabel(m: DraftContent['method']): string {
  return m === 'sendMessage' ? 'обычное сообщение' : 'расширенное сообщение';
}

function entityExtra(entity: MessageEntity): string | null {
  if (entity.type === 'text_link') return entity.url;
  if (entity.type === 'pre') return entity.language ?? null;
  return null;
}

export interface VersionDiffProps {
  readonly result: VersionDiffResult;
  /** Версия-база: левое полотно. Её номер обязан совпадать с `result.against`. */
  readonly base: RenderModel;
  /** Версия-цель: правое полотно. Та же модель, что показывает режим чтения. */
  readonly target: RenderModel;
}

/**
 * Что стало с файлом поста (PDF, docs/release-2026-09-19.md §9.2) — словами, или `null`, если
 * ничего. Ответ маршрута сравнения про документ не знает (флага `document_changed` в нём нет),
 * а обе модели рендера здесь есть: сравнение идёт по подписываемым дескрипторам, то есть по
 * тому же, что входит в хэш. Без этой строки версия, отличающаяся от базы ТОЛЬКО файлом,
 * получила бы вердикт «различий нет».
 */
export function documentChange(
  base: DraftContent['document'] | undefined,
  target: DraftContent['document'] | undefined,
): string | null {
  const a = base ?? null;
  const b = target ?? null;
  if (a === null && b === null) return null;
  if (a === null && b !== null) return `Файл прикреплён: «${b.file_name}» · ${fileSize(b.bytes)}.`;
  if (a !== null && b === null) return `Файл убран: «${a.file_name}».`;
  if (a === null || b === null) return null;
  if (a.sha256 === b.sha256 && a.file_name === b.file_name) return null;
  return a.file_name === b.file_name
    ? `Файл заменён: «${b.file_name}» — другое содержимое, ${fileSize(b.bytes)}.`
    : `Файл заменён: «${a.file_name}» → «${b.file_name}» · ${fileSize(b.bytes)}.`;
}

export function VersionDiff({ result, base, target }: VersionDiffProps): JSX.Element {
  const d = result.diff;
  const textChanged = d.text.some((c) => c.kind !== 'equal');
  const docChange = documentChange(base.canonical.document, target.canonical.document);
  const changed =
    textChanged ||
    d.entities.length > 0 ||
    d.media_changed ||
    d.link_preview_changed ||
    d.method_changed ||
    docChange !== null;

  const sides = useMemo(() => diffSides(d.text), [d.text]);

  /**
   * Пометки ставятся, только если склейка кусков совпала с текстами обеих версий. Иначе
   * ответ сравнения относится не к этим моделям (устаревший запрос, гонка refetch), и
   * подсветить в таком случае — значит зачеркнуть не те слова. Полотна при этом остаются:
   * две версии рядом полезны и без пометок, а расхождение названо словами.
   */
  const aligned =
    sides.baseText === base.canonical.text && sides.targetText === target.canonical.text;

  return (
    <div className={styles['wrap']}>
      <div className={styles['head']}>
        <span className="eyebrow">Сравнение версий</span>
        <span className="meta">
          v{String(result.against)} → v{String(result.version_no)}
        </span>
      </div>

      {changed ? null : (
        <span className="micro">Различий нет: канонические формы обеих версий совпадают.</span>
      )}

      {aligned ? null : (
        <div className="val val-warn">
          <IconWarn />
          <span>
            Пометки не показаны: сравнение посчитано для другого содержимого версий. Обновите экран
            — иначе зачёркнутым окажется не то, что убрали.
          </span>
        </div>
      )}

      {d.coarse ? (
        <div className="val val-info">
          <IconInfo />
          <span>
            Тексты разошлись слишком сильно — посимвольное сравнение заменено сплошным: слева старый
            текст целиком, справа новый.
          </span>
        </div>
      ) : null}

      <div className={styles['canvases']}>
        <section className={styles['side']} aria-label="Было">
          <div className={styles['sideHead']}>
            <span className="eyebrow">было</span>
            <span className="meta">v{String(result.against)}</span>
          </div>
          <div className="tgzone">
            <MessagePreview
              canonical={base.canonical}
              mediaRef={base.media_ref}
              documentRef={base.document_ref ?? null}
              mode="expected"
              theme="light"
              brand={base.brand}
              marks={aligned ? sides.base : null}
            />
          </div>
        </section>

        <section className={styles['side']} aria-label="Стало">
          <div className={styles['sideHead']}>
            <span className="eyebrow">стало</span>
            <span className="meta">v{String(result.version_no)}</span>
          </div>
          <div className="tgzone">
            <MessagePreview
              canonical={target.canonical}
              mediaRef={target.media_ref}
              documentRef={target.document_ref ?? null}
              mode="expected"
              theme="light"
              brand={target.brand}
              marks={aligned ? sides.target : null}
            />
          </div>
        </section>
      </div>

      {d.method_changed ? (
        <div className="val val-warn">
          <IconWarn />
          <span>
            Способ отправки изменился: {methodLabel(base.canonical.method)} →{' '}
            {methodLabel(target.canonical.method)}. Он выбирается при сохранении версии: картинка,
            файл, текст длиннее {SEND_MESSAGE_THRESHOLD} кодпоинтов или настройка «Все посты —
            расширенным сообщением» — расширенное сообщение, иначе обычное. Вместе с ним меняется и
            карточка превью ссылки: у обычного сообщения она есть, у расширенного её не бывает
            вовсе.
          </span>
        </div>
      ) : null}

      {d.media_changed ? (
        <div className="val val-info">
          <IconInfo />
          <span>Медиа изменилось: это другая картинка или другие её настройки.</span>
        </div>
      ) : null}

      {docChange === null ? null : (
        <div className="val val-info">
          <IconInfo />
          <span className={styles['docLine']} data-testid="diff-document">
            {docChange}
          </span>
        </div>
      )}

      {d.link_preview_changed ? (
        <div className="val val-info">
          <IconInfo />
          <span>Настройки карточки превью ссылки изменились.</span>
        </div>
      ) : null}

      {d.entities.length === 0 ? null : (
        <div className={styles['entities']}>
          <span className="meta">Сущности</span>
          {withKeys(d.entities, entityKey).map(({ key, item }) => {
            const extra = entityExtra(item.entity);
            return (
              <div key={key} className={styles['entRow']}>
                {/*
                 * role="img" + aria-label, а не голый знак: «+» и «−» несут единственное
                 * различие между двумя строками, и в звуковом прочтении оно иначе теряется.
                 */}
                <span
                  role="img"
                  className={`${styles['sign']} ${item.kind === 'add' ? styles['add'] : styles['del']}`}
                  aria-label={item.kind === 'add' ? 'добавлена' : 'убрана'}
                >
                  {item.kind === 'add' ? '+' : '−'}
                </span>
                <span>{ENTITY_LABEL_RU[item.entity.type]}</span>
                <span className={styles['range']}>
                  {String(item.entity.offset)}…{String(item.entity.offset + item.entity.length)}
                </span>
                {extra === null ? null : <span className={styles['url']}>{extra}</span>}
              </div>
            );
          })}
          <span className="micro">
            Диапазоны — в единицах UTF-16, как в Bot API. Правка текста сдвигает сущность, поэтому
            «переехала» и «заменена» здесь неразличимы.
          </span>
        </div>
      )}
    </div>
  );
}
